import { Injectable, inject } from '@angular/core';
import { map, Observable } from 'rxjs';

import { ApiClientService } from '../../../core/http/api-client.service';
import {
  AppointmentPendingScopeSummaryDto,
  AppointmentPendingSummaryDto,
  AppointmentPendingSummaryScope,
  AppointmentClientView,
  AppointmentClientViewDto,
  AppointmentChangeRequestFilters,
  AppointmentChangeRequestItem,
  AppointmentChangeRequestItemDto,
  AppointmentChangeRequestPage,
  AppointmentChangeRequestPageDto,
  AppointmentChangeRequestSlotMeta,
  AppointmentChangeRequestSlotMetaDto,
  AppointmentChangeRequestSort,
  AppointmentDetail,
  AppointmentDetailDto,
  AppointmentListSort,
  ClientAppointmentCreateContextDto,
  AppointmentChangeRequestSnapshot,
  AppointmentChangeRequestSnapshotDto,
  AppointmentListFilters,
  AppointmentListItem,
  AppointmentListPageDto,
  AppointmentListPageItem,
  AppointmentListItemDto,
  AppointmentRatingDto,
  AppointmentRatingRequest,
  AppointmentSpecialistSuggestionDto,
  AppointmentSpecialistSuggestionRequest,
  CompleteAppointmentRequest,
  CreateAppointmentChangeRequest,
  CreateAppointmentRequest,
  UpdateAppointmentRequest
} from './appointments.models';
import { mapAppointmentClientView, mapAppointmentDetail, mapAppointmentListItem } from './appointments.mapper';

@Injectable({
  providedIn: 'root'
})
export class AppointmentsService
{
  private readonly apiClient = inject(ApiClientService);

  public getList(
    filters?: AppointmentListFilters
  ): Observable<AppointmentListItem[]>
  {
    const query = filters
      ? {
          status: filters.status,
          clinicId: filters.clinicId,
          deviceId: filters.deviceId,
          clientId: filters.clientId,
          specialistUserId: filters.specialistUserId,
          procedureTypeId: filters.procedureTypeId
        }
      : undefined;

    return this.getMappedList('/appointments', query);
  }

  public getPage(
    params: {
      limit: number;
      cursor?: string | null;
      filters?: AppointmentListFilters;
      sort?: AppointmentListSort;
      pendingFirst?: boolean;
    },
  ): Observable<AppointmentListPageItem>
  {
    const query = {
      status: params.filters?.status,
      clinicId: params.filters?.clinicId,
      deviceId: params.filters?.deviceId,
      clientId: params.filters?.clientId,
      specialistUserId: params.filters?.specialistUserId,
      procedureTypeId: params.filters?.procedureTypeId,
      sort: params.sort,
      pendingFirst: params.pendingFirst,
      limit: params.limit,
      cursor: params.cursor ?? undefined,
    };

    return this.apiClient.get<AppointmentListPageDto>(
      '/appointments',
      query,
    ).pipe(
      map(mapAppointmentListPage),
    );
  }

  public getMyPage(
    params: {
      limit: number;
      cursor?: string | null;
      sort?: AppointmentListSort;
      pendingFirst?: boolean;
      status?: AppointmentListFilters['status'];
    },
  ): Observable<AppointmentListPageItem>
  {
    const query = {
      status: params.status,
      sort: params.sort,
      pendingFirst: params.pendingFirst,
      limit: params.limit,
      cursor: params.cursor ?? undefined,
    };

    return this.apiClient.get<AppointmentListPageDto>(
      '/appointments/my',
      query,
    ).pipe(
      map(mapAppointmentListPage),
    );
  }

  public getById(
    id: string
  ): Observable<AppointmentListItem>
  {
    return this.apiClient.get<AppointmentListItemDto>(
      `/appointments/${id}`
    ).pipe(
      map(mapAppointmentListItem)
    );
  }

  public getDetailById(
    id: string,
  ): Observable<AppointmentDetail>
  {
    return this.apiClient.get<AppointmentDetailDto>(
      `/appointments/${id}/detail`
    ).pipe(
      map(mapAppointmentDetail)
    );
  }

  public getClientViewById(
    id: string,
  ): Observable<AppointmentClientView>
  {
    return this.apiClient.get<AppointmentClientViewDto>(
      `/appointments/${id}/client-view`
    ).pipe(
      map(mapAppointmentClientView)
    );
  }

  public create(
    payload: CreateAppointmentRequest
  ): Observable<AppointmentListItem>
  {
    return this.apiClient.post<AppointmentListItemDto, CreateAppointmentRequest>(
      '/appointments',
      payload
    ).pipe(
      map(mapAppointmentListItem)
    );
  }

  public getClientCreateContext(): Observable<ClientAppointmentCreateContextDto>
  {
    return this.apiClient.get<ClientAppointmentCreateContextDto>(
      '/appointments/create-context'
    );
  }

  public getSpecialistSuggestion(
    params: AppointmentSpecialistSuggestionRequest,
  ): Observable<AppointmentSpecialistSuggestionDto>
  {
    return this.apiClient.get<AppointmentSpecialistSuggestionDto>(
      '/appointments/specialist-suggestion',
      {
        clientId: params.clientId,
        procedureTypeId: params.procedureTypeId,
        clinicId: params.clinicId,
        treatmentId: params.treatmentId,
        schedulingDate: params.schedulingDate,
      },
    );
  }

  public confirm(
    id: string,
  ): Observable<AppointmentListItem>
  {
    return this.apiClient.patch<AppointmentListItemDto>(
      `/appointments/${id}/confirm`,
      undefined
    ).pipe(
      map(mapAppointmentListItem)
    );
  }

  public cancel(
    id: string,
  ): Observable<AppointmentListItem>
  {
    return this.apiClient.patch<AppointmentListItemDto>(
      `/appointments/${id}/cancel`,
      undefined
    ).pipe(
      map(mapAppointmentListItem)
    );
  }

  public complete(
    id: string,
  ): Observable<AppointmentListItem>
  {
    return this.apiClient.patch<AppointmentListItemDto, CompleteAppointmentRequest | undefined>(
      `/appointments/${id}/complete`,
      undefined
    ).pipe(
      map(mapAppointmentListItem)
    );
  }

  public checkIn(
    id: string,
  ): Observable<AppointmentListItem>
  {
    return this.apiClient.patch<AppointmentListItemDto>(
      `/appointments/${id}/check-in`,
      undefined
    ).pipe(
      map(mapAppointmentListItem)
    );
  }

  public finish(
    id: string,
  ): Observable<AppointmentListItem>
  {
    return this.apiClient.patch<AppointmentListItemDto>(
      `/appointments/${id}/finish`,
      undefined
    ).pipe(
      map(mapAppointmentListItem)
    );
  }

  public noShow(
    id: string,
  ): Observable<AppointmentListItem>
  {
    return this.apiClient.patch<AppointmentListItemDto>(
      `/appointments/${id}/no-show`,
      undefined
    ).pipe(
      map(mapAppointmentListItem)
    );
  }

  public rate(
    id: string,
    payload: AppointmentRatingRequest,
  ): Observable<AppointmentRatingDto>
  {
    return this.apiClient.post<AppointmentRatingDto, AppointmentRatingRequest>(
      `/appointments/${id}/rating`,
      payload
    );
  }

  public updateRequest(
    id: string,
    payload: UpdateAppointmentRequest
  ): Observable<AppointmentListItem>
  {
    return this.apiClient.patch<AppointmentListItemDto, UpdateAppointmentRequest>(
      `/appointments/${id}/request`,
      payload
    ).pipe(
      map(mapAppointmentListItem)
    );
  }

  public createChangeRequest(
    appointmentId: string,
    payload: CreateAppointmentChangeRequest
  ): Observable<Record<string, unknown>>
  {
    return this.apiClient.post<Record<string, unknown>, CreateAppointmentChangeRequest>(
      `/appointments/${appointmentId}/change-requests`,
      payload
    );
  }

  public getPendingChangeRequests(): Observable<AppointmentChangeRequestItem[]>
  {
    return this.getChangeRequestsPage({
      limit: 100,
      sort: 'createdAtAsc',
      filters: { status: 'pending' },
    }).pipe(
      map((page) => page.items),
    );
  }

  public getChangeRequestsPage(
    params: {
      limit: number;
      cursor?: string | null;
      filters?: AppointmentChangeRequestFilters;
      sort?: AppointmentChangeRequestSort;
      pendingFirst?: boolean;
    },
  ): Observable<AppointmentChangeRequestPage>
  {
    const query = {
      status: params.filters?.status,
      clinicId: params.filters?.clinicId,
      clientId: params.filters?.clientId,
      procedureTypeId: params.filters?.procedureTypeId,
      deviceId: params.filters?.deviceId,
      specialistUserId: params.filters?.specialistUserId,
      sort: params.sort,
      pendingFirst: params.pendingFirst,
      limit: params.limit,
      cursor: params.cursor ?? undefined,
    };

    return this.apiClient.get<AppointmentChangeRequestPageDto | AppointmentChangeRequestItemDto[]>(
      '/appointments/change-requests/pending',
      query,
    ).pipe(
      map((response) => {
        if (Array.isArray(response)) {
          return {
            items: response.map((item) => this.mapChangeRequest(item)),
            nextCursor: null,
            hasMore: false,
          };
        }

        return {
          items: response.items.map((item) => this.mapChangeRequest(item)),
          nextCursor: response.nextCursor,
          hasMore: response.hasMore,
        };
      }),
    );
  }

  public getPendingSummary(): Observable<AppointmentPendingSummaryDto>
  {
    return this.apiClient.get<AppointmentPendingSummaryDto>(
      '/appointments/pending-summary'
    );
  }

  public getPendingSummaryByScope(
    scope: AppointmentPendingSummaryScope
  ): Observable<AppointmentPendingScopeSummaryDto>
  {
    return this.apiClient.get<AppointmentPendingScopeSummaryDto>(
      `/appointments/pending-summary-scope/${scope}`
    );
  }

  public getChangeRequests(
    appointmentId: string
  ): Observable<AppointmentChangeRequestItem[]>
  {
    return this.apiClient.get<AppointmentChangeRequestItemDto[]>(
      `/appointments/${appointmentId}/change-requests`
    ).pipe(
      map((items) => items.map((item) => this.mapChangeRequest(item)))
    );
  }

  public approveChangeRequest(
    requestId: string,
    note?: string
  ): Observable<AppointmentListItem>
  {
    return this.apiClient.patch<AppointmentListItemDto, { note?: string }>(
      `/appointments/change-requests/${requestId}/approve`,
      note ? { note } : {}
    ).pipe(
      map(mapAppointmentListItem)
    );
  }

  public rejectChangeRequest(
    requestId: string,
    note?: string
  ): Observable<AppointmentChangeRequestItemDto>
  {
    return this.apiClient.patch<AppointmentChangeRequestItemDto, { note?: string }>(
      `/appointments/change-requests/${requestId}/reject`,
      note ? { note } : {}
    );
  }

  private getMappedList(
    path: string,
    query?: Record<string, string | number | boolean | null | undefined>
  ): Observable<AppointmentListItem[]>
  {
    return this.apiClient.get<AppointmentListItemDto[] | null>(
      path,
      query
    ).pipe(
      map((items: AppointmentListItemDto[] | null) =>
      {
        if (!Array.isArray(items))
        {
          return [];
        }

        return items.map(mapAppointmentListItem);
      })
    );
  }

  private mapChangeRequest(item: AppointmentChangeRequestItemDto): AppointmentChangeRequestItem
  {
    const payload = item.payloadJson ?? {};

    return {
      id: item.id,
      appointmentId: item.appointmentId,
      requestedByUserId: item.requestedByUserId,
      requestedByRole: item.requestedByRole,
      status: item.status,
      clientId: item.clientId ?? null,
      clientLabel: item.clientLabel ?? null,
      clientTitle: item.clientTitle ?? null,
      clinicId: item.requestedSnapshot?.clinicId ?? this.readPayloadString(payload, 'clinicId'),
      procedureTypeId:
        item.requestedSnapshot?.procedureTypeId ?? this.readPayloadString(payload, 'procedureTypeId'),
      deviceId: item.requestedSnapshot?.deviceId ?? this.readPayloadString(payload, 'deviceId'),
      specialistUserId:
        item.requestedSnapshot?.specialistUserId ?? this.readPayloadString(payload, 'specialistUserId'),
      slotIds: item.requestedSnapshot?.slotIds ?? this.readPayloadStringArray(payload, 'slotIds'),
      note: typeof payload['note'] === 'string' ? payload['note'] : null,
      reviewNote: item.reviewNote ?? null,
      reviewedAt: item.reviewedAt ?? null,
      createdAt: item.createdAt,
      currentSnapshot: this.readSnapshot(payload, 'current', item.currentSnapshot),
      requestedSnapshot: this.readSnapshot(payload, 'requested', item.requestedSnapshot)
    };
  }

  private readSnapshot(
    payload: Record<string, unknown>,
    key: 'current' | 'requested',
    dto?: AppointmentChangeRequestSnapshotDto | null
  ): AppointmentChangeRequestSnapshot | null
  {
    if (dto) {
      return {
        clinicId: dto.clinicId ?? null,
        procedureTypeId: dto.procedureTypeId ?? null,
        deviceId: dto.deviceId ?? null,
        specialistUserId: dto.specialistUserId ?? null,
        slotIds: Array.isArray(dto.slotIds)
          ? dto.slotIds.filter((slotId): slotId is string => typeof slotId === 'string')
          : [],
        clinicLabel: dto.clinicLabel ?? null,
        clinicTimeZone: dto.clinicTimeZone ?? null,
        procedureTypeLabel: dto.procedureTypeLabel ?? null,
        deviceLabel: dto.deviceLabel ?? null,
        specialistLabel: dto.specialistLabel ?? null,
        slotMetas: this.mapChangeRequestSlotMetas(dto.slotMetas ?? []),
      };
    }

    const raw = payload[key];

    if (!raw || typeof raw !== 'object')
    {
      return null;
    }

    const snapshot = raw as Record<string, unknown>;

    return {
      clinicId: typeof snapshot['clinicId'] === 'string' ? snapshot['clinicId'] : null,
      procedureTypeId:
        typeof snapshot['procedureTypeId'] === 'string' ? snapshot['procedureTypeId'] : null,
      deviceId: typeof snapshot['deviceId'] === 'string' ? snapshot['deviceId'] : null,
      specialistUserId:
        typeof snapshot['specialistUserId'] === 'string' ? snapshot['specialistUserId'] : null,
      slotIds: Array.isArray(snapshot['slotIds'])
        ? snapshot['slotIds'].filter((slotId): slotId is string => typeof slotId === 'string')
        : [],
      clinicLabel: null,
      clinicTimeZone: null,
      procedureTypeLabel: null,
      deviceLabel: null,
      specialistLabel: null,
      slotMetas: [],
    };
  }

  private readPayloadString(payload: Record<string, unknown>, key: string): string | null
  {
    return typeof payload[key] === 'string' ? payload[key] : null;
  }

  private readPayloadStringArray(payload: Record<string, unknown>, key: string): string[]
  {
    return Array.isArray(payload[key])
      ? payload[key].filter((item): item is string => typeof item === 'string')
      : [];
  }

  private mapChangeRequestSlotMetas(
    metas: AppointmentChangeRequestSlotMetaDto[]
  ): AppointmentChangeRequestSlotMeta[]
  {
    return metas
      .map((meta) => ({
        id: meta.id?.trim() ?? '',
        clinicId: meta.clinicId?.trim() ?? '',
        startTime: meta.startTime?.trim() ?? '',
        endTime: meta.endTime?.trim() ?? '',
      }))
      .filter((meta) => !!meta.id && !!meta.clinicId && !!meta.startTime && !!meta.endTime);
  }
}

function mapAppointmentListPage(
  dto: AppointmentListPageDto,
): AppointmentListPageItem
{
  if (!dto || !Array.isArray(dto.items)) {
    console.error('Invalid appointments page response', dto);
    throw new Error('Invalid appointments page response');
  }

  return {
    items: dto.items.map(mapAppointmentListItem),
    nextCursor: dto.nextCursor ?? null,
    hasMore: !!dto.hasMore,
  };
}
